import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { authService, authenticatedFetch, User } from '../services/authService';
import { przekierujDoLogowania } from '../utils/logowaniePortalu';

// Helper to check if token is expired
function isTokenExpired(token: string): boolean {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    const exp = payload.exp;
    if (!exp) return false;
    // Add 30 second buffer
    return Date.now() >= (exp * 1000) - 30000;
  } catch {
    return true;
  }
}

// Helper to clear auth cookie
function clearAuthCookie() {
  const host = window.location.hostname;
  const parts = host.split('.');
  // Clear cookie on current domain and parent domain
  const domains = [host];
  if (parts.length > 2) {
    domains.push(parts.slice(1).join('.'));
  }
  if (parts.length > 3) {
    domains.push(parts.slice(2).join('.'));
  }
  for (const domain of domains) {
    document.cookie = `authToken=; domain=.${domain}; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
  }
  console.log('[AuthContext] Cleared auth cookies for domains:', domains);
}

// Ponowienia sprawdzenia sesji, gdy serwer chwilowo nie odpowiada (restart backendu przy deployu).
const PONOWIENIA_SESJI_MS = [1000, 2000, 4000, 8000];

// Authentication context interface
interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (login: string, haslo: string) => Promise<boolean>;
  logout: () => void;
  updateLastLogin: () => void;
  hasRole: (roleName: string) => boolean;
  isAdmin: () => boolean;
  hasRegion: (regionName: string) => boolean;
  hasAnyRegion: (regionNames: string[]) => boolean;
  canAccessRegionData: (dataRegion: string) => boolean;
  getUserRegions: () => string[];
}

// Create the context
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Auth Provider Props
interface AuthProviderProps {
  children: ReactNode;
}

// Auth Provider Component
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Check if user is already logged in on app start
  useEffect(() => {
    const checkAuthStatus = async () => {
      let przekierowano = false;
      try {
        // First, try to get token from sessionStorage (works if same domain)
        let token: string | null = null;
        try {
          token = sessionStorage.getItem('authToken');
          
          // Check if existing token is expired
          if (token && isTokenExpired(token)) {
            sessionStorage.removeItem('authToken');
            clearAuthCookie();
            token = null;
          }
        } catch {
          // sessionStorage not available
        }
        
        // If no token and we're on a different subdomain, try to request from parent portal
        if (!token) {
          try {
            // Dynamic import to avoid circular dependencies
            const { requestTokenFromParent } = await import('../utils/authBridge');
            
            token = await requestTokenFromParent();
            
            if (token) {
              // Check if token is expired BEFORE using it
              if (isTokenExpired(token)) {
                clearAuthCookie();
                try { sessionStorage.removeItem('authToken'); } catch {}
                przekierowano = przekierujDoLogowania();
                return;
              }
              
              try {
                sessionStorage.setItem('authToken', token);
                // Small delay to ensure token is available for axios interceptor
                await new Promise(resolve => setTimeout(resolve, 50));
              } catch {
                // Failed to store token
              }
            }
          } catch {
            // Failed to request token from parent
          }
        }
        
        // Verify token is available before making API call
        let finalToken: string | null = null;
        try {
          finalToken = sessionStorage.getItem('authToken');
        } catch {
          // sessionStorage not available
        }
        
        if (!finalToken) {
          // Bez tokenu modul nic nie pokaze (kazde zapytanie czeka na sesje), wiec od razu do
          // logowania w portalu zamiast pustej strony, ktora wygladala na zawieszona.
          przekierowano = przekierujDoLogowania();
          return;
        }

        // Always ask backend; sessionStorage token is the source of truth.
        // Sesja niewazna (null) -> logowanie w portalu. Blad sieci albo serwera -> kilka ponowien
        // bez kasowania tokenu: wczesniej kazdy blad kasowal token i wspolne ciasteczko portalu,
        // wiec odswiezenie strony w trakcie deployu wylogowywalo ze wszystkich modulow.
        let userData: User | null | undefined;
        for (let proba = 0; userData === undefined; proba++) {
          try {
            userData = await authService.getCurrentUser();
          } catch (error) {
            if (proba >= PONOWIENIA_SESJI_MS.length) {
              console.error('[AuthContext] Serwer nie odpowiada, token zostaje do odświeżenia strony:', error);
              break;
            }
            await new Promise((resolve) => setTimeout(resolve, PONOWIENIA_SESJI_MS[proba]));
          }
        }
        if (userData) {
          setUser(userData);
        } else if (userData === null) {
          try { sessionStorage.removeItem('authToken'); } catch {}
          clearAuthCookie();
          przekierowano = przekierujDoLogowania();
        }
      } catch (error) {
        // Nieoczekiwany blad tego kodu nie dowodzi niewaznej sesji - token zostaje.
        console.error('[AuthContext] Auth check failed:', error);
      } finally {
        // Przy przekierowaniu zostajemy w „ladowaniu” do nawigacji, bez migniecia pustej strony.
        if (!przekierowano) setIsLoading(false);
      }
    };

    checkAuthStatus();
  }, []);

  const login = async (login: string, haslo: string): Promise<boolean> => {
    try {
      setIsLoading(true);
      
      // Add a small delay to prevent rapid requests
      await new Promise(resolve => setTimeout(resolve, 500));
      
      const response = await authService.login(login, haslo);
      
      setUser(response.user);
      // Store token in sessionStorage (isolated per tab/window) instead of localStorage (shared)
      // This allows different users to be logged in different browser tabs/windows
      if (response.token) {
        try {
          sessionStorage.setItem('authToken', response.token);
        } catch (e) {
          console.warn('Failed to store token in sessionStorage:', e);
        }
      }
      return true;
    } catch (error: any) {
      console.error('Login failed:', error);
      
      // Handle specific error types
      if (error.response?.status === 429) {
        console.error('Rate limit exceeded. Please wait a moment before trying again.');
        // You could show a user-friendly message here
      } else if (error.response?.status === 401) {
        console.error('Invalid credentials');
      } else if (error.response?.status >= 500) {
        console.error('Server error. Please try again later.');
      }
      
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    try {
      await authenticatedFetch('/api/auth/logout', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' }
      });
    } catch {}
    try { sessionStorage.removeItem('authToken'); } catch {}
    setUser(null);
    try {
      // Always redirect to portal login (avoid module login routes that can loop)
      const host = window.location.hostname || '';
      const parts = host.split('.');
      if (parts.length >= 3) {
        // module.tenant.domain -> tenant.domain
        const tenantHost = parts.slice(1).join('.');
        window.location.assign(`https://${tenantHost}/`);
      } else {
        window.location.assign('/');
      }
    } catch {}
  };

  const updateLastLogin = () => {
    if (user) {
      setUser({
        ...user,
        ostatnie_logowanie: new Date().toISOString()
      });
    }
  };

  const hasRole = (roleName: string): boolean => {
    return authService.hasRole(user, roleName);
  };

  const isAdmin = (): boolean => {
    return authService.isAdmin(user);
  };

  const hasRegion = (regionName: string): boolean => {
    return authService.hasRegion(user, regionName);
  };

  const hasAnyRegion = (regionNames: string[]): boolean => {
    return authService.hasAnyRegion(user, regionNames);
  };

  const canAccessRegionData = (dataRegion: string): boolean => {
    return authService.canAccessRegionData(user, dataRegion);
  };

  const getUserRegions = (): string[] => {
    return authService.getUserRegions(user);
  };

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    isLoading,
    login,
    logout,
    updateLastLogin,
    hasRole,
    isAdmin,
    hasRegion,
    hasAnyRegion,
    canAccessRegionData,
    getUserRegions
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to use auth context
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}; 